import React, { useEffect, useRef, useState, memo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Animated, Easing, Platform, Dimensions } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { productService } from '../services/ProductService';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/* -------------------------------------------------------------------------- */
/*                                  INSTELLINGEN                              */
/* -------------------------------------------------------------------------- */

const SCHERM_BREEDTE = Dimensions.get('window').width;
const SCAN_GROOTTE = Math.min(SCHERM_BREEDTE * 0.8, 300);

// Mooie kleuren voor de ap
const KLEUREN = {
    achtergrond: '#0F172A',
    hoofdKleur: '#8B5CF6',
    tweedeKleur: '#0EA5E9',
    accent: '#F43F5E',
    tekst: '#F8FAFC',
    tekstGrijs: '#94A3B8',
    glas: 'rgba(30, 41, 59, 0.7)',
    glasLicht: 'rgba(255, 255, 255, 0.1)',
    goed: '#10B981',
    fout: '#EF4444',
    randje: 'rgba(14, 165, 233, 0.5)',
};

/* -------------------------------------------------------------------------- */
/*                                 COMPONENTEN                                */
/* -------------------------------------------------------------------------- */

// Dit is een speciaal bakje zodat React niet in de war raakt
// Anders crasht de scanner steeds omdat HTML5-QRCode nogal eigenwijs is
const ScannerBakje = memo(() => {
    return (
        <View 
            nativeID="reader" 
            style={stijlen.readerView} 
        />
    );
});

// De laser straal die heen en weer gaat (pew pew)
const LaserStraal = ({ animatieWaarde, isGelukt }: { animatieWaarde: Animated.Value, isGelukt: boolean }) => {
    // Beweeg van boven naar beneden
    const beweegY = animatieWaarde.interpolate({
        inputRange: [0, 1],
        outputRange: [0, SCAN_GROOTTE],
    });

    // Als we iets gevonden hebben, stop de laser
    if (isGelukt) return null;

    return (
        <Animated.View
            style={[
                stijlen.laser,
                {
                    transform: [{ translateY: beweegY }],
                    opacity: 0.9,
                },
            ]}
        >
            <View style={stijlen.laserKern} />
            <View style={stijlen.laserGloed} />
        </Animated.View>
    );
};

// Gewoon een mooi hoekje voor de sier
const Hoekje = ({ positie, stijltjes }: { positie: string, stijltjes?: any }) => {
    return <View style={[stijlen.hoek, stijltjes]} />;
};

/* -------------------------------------------------------------------------- */
/*                                HOOFD SCHERM                                */
/* -------------------------------------------------------------------------- */

export const ScannerScreen: React.FC<any> = ({ navigation }) => {
    const { t } = useTranslation();
    const insets = useSafeAreaInsets();

    // Alle variabelen (state) die we nodig hebben
    const [aanHetLaden, setAanHetLaden] = useState(true);
    const [foutMelding, setFoutMelding] = useState<string | null>(null);
    const [isBezig, setIsBezig] = useState(false);
    const [cameraLijst, setCameraLijst] = useState<any[]>([]); // Hier komen alle cameras in
    const [gekozenCameraId, setGekozenCameraId] = useState<string | null>(null);
    const [scanGelukt, setScanGelukt] = useState(false);
    // Dingen bewaren zonder opnieuw te renderen
    const laserAnimatie = useRef(new Animated.Value(0)).current;
    const scannerDing = useRef<Html5Qrcode | null>(null);
    const isGemonteerd = useRef(true);

    // Starten als het scherm opent
    useEffect(() => {
        isGemonteerd.current = true;
        
        // Even wachten zodat de HTML zeker weten klaar is (DOM moet er zijn)
        const timer = setTimeout(() => {
            allesOpstarten();
            startLaserAnimatie();
        }, 100);

        // Opruimen als we weggaan anders loopt het geheugen vol
        return () => {
            isGemonteerd.current = false;
            clearTimeout(timer);
            stopDeScanner().catch(console.error);
        };
    }, []);

    const startLaserAnimatie = () => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(laserAnimatie, {
                    toValue: 1,
                    duration: 2000,
                    easing: Easing.inOut(Easing.quad),
                    useNativeDriver: false,
                }),
                Animated.timing(laserAnimatie, {
                    toValue: 0,
                    duration: 2000,
                    easing: Easing.inOut(Easing.quad),
                    useNativeDriver: false,
                })
            ])
        ).start();
    };

    const stopDeScanner = async () => {
        try {
            if (scannerDing.current) {
                // Probeer te stoppen als ie aan staat
                try {
                     if (scannerDing.current.isScanning) {
                        await scannerDing.current.stop();
                     }
                } catch (e) {
                    console.log("Kon niet stoppen (misschien stond ie al uit):", e);
                }
                scannerDing.current.clear();
                scannerDing.current = null;
            }
        } catch (fout) {
            console.warn("Waarschuwing bij stoppen:", fout);
        }
    };

    const allesOpstarten = async () => {
        setAanHetLaden(true);
        setFoutMelding(null);

        // Zeker weten dat de oude weg is
        await stopDeScanner();

        try {
            // Wachten tot het element er is in de HTML
            let pogingen = 0;
            // @ts-ignore - Document bestaat gewoon
            while (!document.getElementById('reader') && pogingen < 10) {
                await new Promise(r => setTimeout(r, 200));
                pogingen++;
            }

            // @ts-ignore
            if (!document.getElementById('reader')) {
                throw new Error("HTML element niet gevonden. Ververs de pagina aub.");
            }

            // Kijken of het mobiel of pc is (via userAgent hack).
            // Op mobiel willen we de achtercamera, op PC de webcam.
            // En op tablets (volgens de user) de VOORKANT.
            // @ts-ignore
            const userAgent = navigator.userAgent.toLowerCase();
            const isTablet = /ipad|tablet|playbook|silk/.test(userAgent) || 
                             (/android/.test(userAgent) && !/mobile/.test(userAgent));
            
            // Een "echte" telefoon is dus mobiel MAAR geen tablet
            const isPhone = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent) && !isTablet;

            // Configureren wat we gaan starten
            let cameraConfig;
            
            if (isPhone) {
                // Op telefoons: achterkant (environment)
                cameraConfig = { facingMode: "environment" };
                setGekozenCameraId("environment");
            } else {
                 // Tablet of Desktop -> Voorkant (user) of kiezen uit lijst
                 try {
                    const apparaten = await Html5Qrcode.getCameras();
                    setCameraLijst(apparaten);
                    if (apparaten && apparaten.length > 0) {
                         // We zoeken de voorkant
                        const voorkant = apparaten.find(d => 
                            d.label.toLowerCase().includes('front') || 
                            d.label.toLowerCase().includes('user') ||
                            d.label.toLowerCase().includes('face')
                        );
                        // Als we geen voorkant vinden pakken we de eerste
                        const gekozenId = voorkant ? voorkant.id : apparaten[0].id;
                        cameraConfig = gekozenId; // Geef ID door
                        setGekozenCameraId(gekozenId);
                    } else {
                         // Geen lijst? Gewoon user
                        cameraConfig = { facingMode: "user" };
                        setGekozenCameraId("user");
                    }
                 } catch (e) {
                     // Geen permissie voor lijst? Gukken op user
                     cameraConfig = { facingMode: "user" };
                     setGekozenCameraId("user");
                 }
            }
            
            // Even wachten voor de zekerheid
            await new Promise(r => setTimeout(r, 500));
            await startCamera(cameraConfig);

        } catch (fout: any) {
            console.error("Initialisatie mislukt", fout);
            if (isGemonteerd.current) {
                setFoutMelding("Kon de camera niet starten.");
                setAanHetLaden(false);
            }
        }
    };

    // Functie om de camera echt te starten
    const startCamera = async (cameraConfig: string | { facingMode: string }, pogingenOver = 2) => {
        if (!isGemonteerd.current) return;
        
        // Oude opruimen
        if (scannerDing.current) {
            try {
                if (scannerDing.current.isScanning) {
                    await scannerDing.current.stop();
                }
                scannerDing.current.clear();
                scannerDing.current = null;
            } catch (e) {
                console.warn("Opruim foutje:", e);
            }
        }

        // Even wachten
        await new Promise(r => setTimeout(r, 100));

        // @ts-ignore - Document checken
        const readerElement = document.getElementById('reader');
        if (!readerElement) {
            setFoutMelding("Kan scanner bakje niet vinden. Ververs ff.");
            setAanHetLaden(false);
            return;
        }

        // @ts-ignore - Library aanroepen
        const html5QrCode = new Html5Qrcode("reader", {
            formatsToSupport: [ 
                Html5QrcodeSupportedFormats.EAN_13, 
                Html5QrcodeSupportedFormats.EAN_8,
                Html5QrcodeSupportedFormats.UPC_A,
                Html5QrcodeSupportedFormats.UPC_E,
                Html5QrcodeSupportedFormats.QR_CODE,
                Html5QrcodeSupportedFormats.CODE_128,
            ],
            verbose: false,
            // @ts-ignore - Dit maakt het sneller
            experimentalFeatures: {
                useBarCodeDetectorIfSupported: true
            }
        });
        scannerDing.current = html5QrCode;

        try {
            await html5QrCode.start(
                cameraConfig,
                {
                    fps: 25, 
                    qrbox: { width: SCAN_GROOTTE, height: SCAN_GROOTTE },
                    aspectRatio: 1.0,
                    // @ts-ignore
                    videoConstraints: {
                        focusMode: 'continuous',
                        // Probeer hoge resolutie voor scherpte
                        width: { min: 640, ideal: 1280, max: 1920 },
                        height: { min: 480, ideal: 720, max: 1080 },
                        facingMode: typeof cameraConfig === 'object' ? cameraConfig.facingMode : undefined
                    }
                },
                codeGevonden,
                (err) => {
                    // Fouten negeren we, anders wordt de console spam
                }
            );
            
            if (isGemonteerd.current) {
                setAanHetLaden(false);
            }
        } catch (fout: any) {
            console.error("Starten mislukt:", fout);
            
            if (isGemonteerd.current) {
                const foutBericht = fout?.message || String(fout);
                
                if (foutBericht.includes('NotReadable') || foutBericht.includes('TrackStartError')) {
                     // Probeer opnieuw als ie bezet is
                     if (pogingenOver > 0) {
                         console.log(`Druk bezig (nog ${pogingenOver} keer proberen)...`);
                         await new Promise(r => setTimeout(r, 1500));
                         await stopDeScanner();
                         return startCamera(cameraConfig, pogingenOver - 1);
                     }
                    setFoutMelding(t('errors.cameraNotReadable') || "Camera is bezet.");
                } else if (foutBericht.includes('Permission') || foutBericht.includes('NotAllowed')) {
                    setFoutMelding(t('errors.cameraPermissionDenied'));
                } else if (foutBericht.includes('NotFound') || foutBericht.includes('Device')) {
                    setFoutMelding(t('errors.noCamera'));
                } else {
                    setFoutMelding(t('errors.unknownError'));
                }
                setAanHetLaden(false);
            }
        }
    };

    // Als we een barcode hebben gevonden
    const codeGevonden = async (gelezenTekst: string) => {
        if (isBezig || !isGemonteerd.current || scanGelukt) return; // Niet dubbel doen
        
        console.log(`Jaaaa gevonden: ${gelezenTekst}`);
        setIsBezig(true);
        setScanGelukt(true);

        // Pauzeer het beeld
        if (scannerDing.current && scannerDing.current.isScanning) {
            try {
                scannerDing.current.pause(true);
            } catch (e) {
                // Boeit niet
            }
        }

        // Even gebruiker kietelen met succes schermpje
        // Even gebruiker kietelen met succes schermpje
        await new Promise(r => setTimeout(r, 150));

        try {
            const product = await productService.getProductByBarcode(gelezenTekst);
            
            if (product) {
                navigation.replace('ProductDetail', { productId: product.id });
            } else {
                // @ts-ignore - Window alert is er altijd
                const wilZoeken = window.confirm(t('errors.productNotFound') || `Code ${gelezenTekst} niet gevonden in onze database. Wil je zoeken?`);
                if (wilZoeken) {
                     navigation.navigate('SearchTab', { screen: 'Search', params: { query: gelezenTekst } });
                } else {
                    // Doorgaan
                    setScanGelukt(false);
                    setIsBezig(false);
                    if (scannerDing.current) scannerDing.current.resume();
                }
            }
        } catch (fout) {
            console.error("Zoekfout", fout);
            navigation.replace('ProductDetail', { productId: null, barcode: gelezenTekst });
        }
    };

    // Als de gebruiker zelf wil typen
    const handmatigKlik = () => {
         navigation.navigate('SearchTab', { screen: 'Search' });
    };

    // Camera omdraaien
    const draaiCameraOm = () => {
        // Simpele wissel tussen user en environment
        const nieuweModus = gekozenCameraId === "environment" ? "user" : "environment";
        setGekozenCameraId(nieuweModus);
        startCamera({ facingMode: nieuweModus }, 1);
    };

    return (
        <View style={[stijlen.container, { paddingTop: insets.top }]}>
            {/* Achtergrond Laag */}
            <View style={stijlen.cameraLaag}>
                <ScannerBakje />
            </View>

            {/* De mooie laag eroverheen - ZONDER donkere achtergrond over alles */}
            <View style={stijlen.overlay}>
                
                {/* Bovenbalk */}
                <View style={[stijlen.bovenbalk, { marginTop: Platform.OS === 'web' ? 20 : 0 }]}>
                    <TouchableOpacity 
                        style={stijlen.terugKnop}
                        onPress={() => navigation.goBack()}
                    >
                         <Text style={stijlen.terugIcoon}>←</Text>
                    </TouchableOpacity>
                    <View style={stijlen.titelBak}>
                        <Text style={stijlen.kleineTitel}>{t('scanner.system')}</Text>
                        <Text style={stijlen.groteTitel}>{t('scanner.scannerTitle')}</Text>
                    </View>
                    <View style={{ width: 44 }} /> 
                </View>

                {/* Scan Gebied */}
                <View style={stijlen.scanGebied}>
                   <View style={[
                       stijlen.scanKader,
                       scanGelukt && stijlen.scanKaderGelukt
                   ]}>
                        {!scanGelukt && <LaserStraal animatieWaarde={laserAnimatie} isGelukt={scanGelukt} />}
                        
                        {/* Rastertje */}
                        <View style={stijlen.rasterEffect} />

                        {/* Hoekjes */}
                        <Hoekje positie="linksboven" stijltjes={stijlen.linksBoven} />
                        <Hoekje positie="rechtsboven" stijltjes={stijlen.rechtsBoven} />
                        <Hoekje positie="linksonder" stijltjes={stijlen.linksOnder} />
                        <Hoekje positie="rechtsonder" stijltjes={stijlen.rechtsOnder} />

                        {/* Laden of Bezig Status */}
                        {(aanHetLaden || isBezig) && (
                            <View style={stijlen.statusLaag}>
                                {scanGelukt ? (
                                    <View>
                                       <Text style={{fontSize:40}}>✅</Text>
                                    </View>
                                ) : (
                                    <>
                                        <ActivityIndicator size="large" color={KLEUREN.tweedeKleur} />
                                        <Text style={stijlen.statusTekst}>
                                            {aanHetLaden ? t('scanner.initializing') : t('scanner.analyzing')}
                                        </Text>
                                    </>
                                )}
                            </View>
                        )}
                   </View>
                   
                   <View style={stijlen.infoPil}>
                       <Text style={stijlen.infoTekst}>
                           {scanGelukt ? t('scanner.codeCaptured') : t('scanner.alignBarcode')}
                       </Text>
                   </View>
                </View>

                {/* Knoppen onderin */}
                <View style={stijlen.onderbalk}>
                    {cameraLijst.length > 1 && (
                         <TouchableOpacity style={stijlen.icoonKnop} onPress={draaiCameraOm}>
                             <Text style={stijlen.icoonKnopTekst}>🔄 {t('scanner.flipCamera')}</Text>
                        </TouchableOpacity>
                    )}

                    <TouchableOpacity style={stijlen.handmatigKnop} onPress={handmatigKlik}>
                        <Text style={stijlen.handmatigKnopTekst}>{t('scanner.enterManually')}</Text>
                    </TouchableOpacity>
                </View>

                {/* Foutmelding */}
                {foutMelding && (
                    <View style={stijlen.foutBanier}>
                        <Text style={stijlen.foutTekst}>{foutMelding}</Text>
                        <TouchableOpacity style={stijlen.probeerOpnieuwKnop} onPress={allesOpstarten}>
                            <Text style={stijlen.probeerOpnieuwTekst}>{t('common.retry').toUpperCase()}</Text>
                        </TouchableOpacity>
                    </View>
                )}
            </View>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*                                   STIJLEN                                  */
/* -------------------------------------------------------------------------- */

const stijlen = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: KLEUREN.achtergrond,
        overflow: 'hidden',
    },
    cameraLaag: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: '#000',
        zIndex: 0,
        justifyContent: 'center',
        alignItems: 'center',
    },
    readerView: {
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 10,
        backgroundColor: 'transparent',
    },
    bovenbalk: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingVertical: 15,
    },
    terugKnop: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: 'rgba(255,255,255,0.1)',
        justifyContent: 'center',
        alignItems: 'center',
        backdropFilter: 'blur(10px)',
    } as any,
    terugIcoon: {
        color: KLEUREN.tekst,
        fontSize: 24,
    },
    titelBak: {
        alignItems: 'center',
    },
    kleineTitel: {
        color: KLEUREN.tweedeKleur,
        fontSize: 10,
        letterSpacing: 4,
        fontWeight: '800',
    },
    groteTitel: {
        color: KLEUREN.tekst,
        fontSize: 18,
        letterSpacing: 2,
        fontWeight: '600',
    },
    scanGebied: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    scanKader: {
        width: SCAN_GROOTTE,
        height: SCAN_GROOTTE,
        borderRadius: 24,
        position: 'relative',
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: 'rgba(14, 165, 233, 0.3)',
        backgroundColor: 'rgba(255,255,255,0.02)',
    },
    scanKaderGelukt: {
        borderColor: KLEUREN.goed,
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
    },
    rasterEffect: {
        ...StyleSheet.absoluteFillObject,
        opacity: 0.1,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
    } as any,
    hoek: {
        position: 'absolute',
        width: 40,
        height: 40,
        borderColor: KLEUREN.tweedeKleur,
        borderWidth: 4,
        opacity: 0.8,
    },
    linksBoven: { top: 0, left: 0, borderRightWidth: 0, borderBottomWidth: 0, borderTopLeftRadius: 20 },
    rechtsBoven: { top: 0, right: 0, borderLeftWidth: 0, borderBottomWidth: 0, borderTopRightRadius: 20 },
    linksOnder: { bottom: 0, left: 0, borderRightWidth: 0, borderTopWidth: 0, borderBottomLeftRadius: 20 },
    rechtsOnder: { bottom: 0, right: 0, borderLeftWidth: 0, borderTopWidth: 0, borderBottomRightRadius: 20 },
    
    laser: {
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 50,
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    laserKern: {
        width: '100%',
        height: 2,
        backgroundColor: KLEUREN.accent,
        shadowColor: KLEUREN.accent,
        shadowRadius: 10,
        shadowOpacity: 1,
    },
    laserGloed: {
        position: 'absolute',
        bottom: 2, 
        left: 0, right: 0,
        height: 48,
        backgroundImage: 'linear-gradient(to top, rgba(244, 63, 94, 0.5), rgba(244, 63, 94, 0))',
    } as any,
    
    statusLaag: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(15, 23, 42, 0.85)',
        justifyContent: 'center',
        alignItems: 'center',
        backdropFilter: 'blur(8px)',
    } as any,
    statusTekst: {
        color: KLEUREN.tweedeKleur,
        marginTop: 15,
        fontSize: 12,
        fontWeight: 'bold',
        letterSpacing: 2,
    },
    infoPil: {
        marginTop: 40,
        backgroundColor: 'rgba(255,255,255,0.08)',
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 30,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(5px)',
    } as any,
    infoTekst: {
        color: KLEUREN.tekst,
        fontSize: 12,
        fontWeight: '600',
        letterSpacing: 2,
    },
    foutBanier: {
        position: 'absolute',
        bottom: 150,
        left: 20, 
        right: 20,
        backgroundColor: 'rgba(239, 68, 68, 0.15)',
        borderWidth: 1,
        borderColor: KLEUREN.fout,
        padding: 16,
        borderRadius: 12,
        alignItems: 'center',
    },
    foutTekst: {
        color: KLEUREN.fout,
        textAlign: 'center',
        marginBottom: 10,
        fontSize: 13,
    },
    probeerOpnieuwKnop: {
        backgroundColor: KLEUREN.fout,
        paddingHorizontal: 20,
        paddingVertical: 8,
        borderRadius: 20,
    },
    probeerOpnieuwTekst: {
        color: '#FFF',
        fontWeight: 'bold',
        fontSize: 12,
        letterSpacing: 1,
    },
    onderbalk: {
        padding: 40,
        alignItems: 'center',
        gap: 20,
    },
    handmatigKnop: {
        width: '100%',
        maxWidth: 320,
        height: 54,
        borderRadius: 27,
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
        justifyContent: 'center',
        alignItems: 'center',
        backdropFilter: 'blur(10px)',
    } as any,
    handmatigKnopTekst: {
        color: KLEUREN.tekst,
        fontWeight: '700',
        fontSize: 13,
        letterSpacing: 1.5,
    },
    icoonKnop: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 12,
        paddingHorizontal: 20,
        borderRadius: 25,
        backgroundColor: KLEUREN.glas,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)',
    },
    icoonKnopTekst: {
        color: KLEUREN.tekst,
        marginLeft: 8,
        fontSize: 12,
        fontWeight: '600',
        letterSpacing: 1,
    }
});
